import {
  amplitudeApiKey,
  amplitudeDebug,
  amplitudeReverseProxy,
  intercomApi,
  intercomId,
  isCloud,
  posthogApiKey,
  posthogReverseProxy,
} from "@/environment";

import { appLoaded } from "./events";
import {
  identifyAmplitudeUser,
  initAmplitude,
  setAmplitudeOptOut,
  debugAmplitude,
} from "./impl/amplitude";
import { initIntercom } from "./impl/intercom";
import { identifyPosthogUser, initPosthog, startPosthogSessionRecording } from "./impl/posthog";
import { trackEvent } from "./track";

// Query param the register flow puts on its post-signup redirect. Matches
// the PostHog URL trigger that pi-web uses to start recording as well.
const REGISTRATION_RECORDING_SOURCE = "registration";

/**
 * Initialize analytics for anonymous (pre-login) tracking. Loads PostHog and
 * Amplitude so public pages (register, login) can capture events scoped to a
 * device id. Post-login `initTracking` re-identifies the same instances so
 * anonymous events stitch to the user.
 */
export function initAnonymousTracking() {
  debugAmplitude(amplitudeDebug);
  if (!isCloud) return;

  if (posthogApiKey) {
    const posthogConfig = { apiKey: posthogApiKey, reverseProxy: posthogReverseProxy };
    void initPosthog(posthogConfig);
  }

  if (amplitudeApiKey) {
    const amplitudeConfig = { apiKey: amplitudeApiKey, reverseProxy: amplitudeReverseProxy };
    void initAmplitude(amplitudeConfig);
  }
}

/**
 * Initialize analytics for an authenticated user.
 */
export function initTracking(user, license) {
  if (!isCloud) return;
  identifyPosthogUser(user, license);

  if (amplitudeApiKey) {
    const amplitudeConfig = { apiKey: amplitudeApiKey, reverseProxy: amplitudeReverseProxy };
    void initAmplitude(amplitudeConfig).then(() => {
      if (license.tracking) {
        // Clear any persisted opt-out (Amplitude stores it in localStorage)
        // before identifying — otherwise the identify call is silently dropped.
        setAmplitudeOptOut(false);
        identifyAmplitudeUser(license, user);
        trackEvent(appLoaded());
      } else {
        setAmplitudeOptOut(true);
      }
    });
  }

  if (!license.tracking) return;

  if (consumeRegistrationRecordingMarker()) startPosthogSessionRecording();

  if (intercomId && intercomApi) {
    const intercomConfig = { id: intercomId, api: intercomApi };
    initIntercom(intercomConfig, license, user);
  }
}

export function startSessionRecording() {
  if (!isCloud) return;
  startPosthogSessionRecording();
}

// Check for registration argument in URL to start recording
// Strip it away, so that a full page re-load does not trigger the recording again. This is to prevent
// ongoing recordings for users which do not directly come from the signup flow (the param might be kept in the URL of the browser by accident)
function consumeRegistrationRecordingMarker() {
  const url = new URL(window.location.href);
  if (url.searchParams.get("from") !== REGISTRATION_RECORDING_SOURCE) return false;

  url.searchParams.delete("from");
  window.history.replaceState(window.history.state, "", url.toString());
  return true;
}
